import { createContext, useContext, ReactNode, startTransition, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { LoginCredentials, AuthResponse, LoginResult, TwoFactorChallenge } from '../types/auth';
import { User } from '../types/user';

type AuthContextType = {
  isCaptchaVerified: boolean;
  user: User | null;
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  logout: () => Promise<void>;
  captcha: (token: string) => Promise<any>;
  updateUser: (user: User) => void;
  isAuthenticated: boolean;
  awaitingOtp: TwoFactorChallenge | null;
  verifyOtp: (code: string, rememberDevice: boolean) => Promise<AuthResponse>;
  resendOtp: () => Promise<void>;
  cancelOtp: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(false);
  const auth = useAuth();

  const updateUser = (updatedUser: User) => {
    if (auth.user && updatedUser.id === auth.user.id) {
      auth.setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isCaptchaVerified: isCaptchaVerified,
        user: auth.user,
        isAuthenticated: auth.isAuthenticated,
        updateUser,
        awaitingOtp: auth.awaitingOtp,
        verifyOtp: auth.verifyOtp,
        resendOtp: auth.resendOtp,
        cancelOtp: auth.cancelOtp,
        login: async (credentials) => {
          const response = await auth.login(credentials);
          startTransition(() => {
            // Any UI state updates related to login can go here
          });
          return response;
        },
        logout: async () => {
          await auth.logout();
          startTransition(() => {
            // Any UI state updates related to logout can go here
          });
        },
        captcha: async (token) => {
          await auth.captcha(token);
          startTransition(() => {
            setIsCaptchaVerified(true);
          });
        }
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
